import React, { useState, useEffect } from 'react';
import API, { UPLOADS_URL } from '../api/axios';
import PageBanner from '../components/PageBanner';

const AdmissionGuidelines = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, sectionRes] = await Promise.all([
          API.get('/pages/public/admission-guidelines'),
          API.get('/home-sections?keys=page_admission_guidelines')
        ]);
        setPage(pageRes.data.data);
        if (sectionRes.data?.data?.page_admission_guidelines) {
          setData(sectionRes.data.data.page_admission_guidelines);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="loading-screen"><i className="fas fa-spinner fa-spin"></i></div>;
  if (!data) return <div className="container py-20 text-center">Data not found.</div>;

  return (
    <>
      <PageBanner 
        title={page?.banner_heading || 'Admission Guidelines'} 
        subtitle={page?.banner_sub_heading || ""} 
        breadcrumbs={[{ label: 'Home', link: '/' }, { label: page?.title || 'Admission Guidelines' }]} 
        backgroundImage={page?.banner_image ? `${UPLOADS_URL}/${page.banner_image}` : null} 
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/why-choose-us.jpg`} 
      />

      <div className="container mx-auto px-[15px] py-12 space-y-12">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-primary mb-4">Academic Session 2027-28</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full"></div>
        </div>

        {/* Age Criteria */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full opacity-50"></div>
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-child text-primary bg-primary/10 w-12 h-12 flex items-center justify-center rounded-xl"></i> 
              Age Criteria
            </h3>
            <ul className="space-y-4 text-gray-600 text-lg">
              {['Pre-Nursery', 'Nursery', 'LKG', 'UKG', 'Class 1st'].map(level => data.age_criteria?.[level] && (
                <li key={level} className="flex items-start gap-3">
                  <i className="fas fa-check-circle text-green-500 mt-1.5"></i> 
                  <strong>{level}:</strong> {data.age_criteria[level]}
                </li>
              ))}
            </ul>
            {data.age_criteria?.note && (
              <p className="mt-6 text-sm italic text-gray-500 border-l-4 border-primary pl-4 bg-gray-50 py-3 pr-3">
                {data.age_criteria.note}
              </p>
            )}
          </div>
        </div>

        {/* Entrance Procedure */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <i className="fas fa-clipboard-check text-primary bg-primary/10 w-12 h-12 flex items-center justify-center rounded-xl"></i> 
            Entrance Procedure
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 transition hover:shadow-md hover:-translate-y-1">
              <h4 className="font-bold text-lg mb-2 text-primary">Pre-Nursery to UKG</h4>
              <p className="text-gray-600">{data.entrance_procedure?.pre_nursery_ukg}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 transition hover:shadow-md hover:-translate-y-1">
              <h4 className="font-bold text-lg mb-2 text-primary">Class I onwards</h4>
              <p className="text-gray-600">{data.entrance_procedure?.class_1_onwards}</p>
            </div>
          </div>
          
          {data.entrance_procedure?.notes?.length > 0 && (
            <div className="mt-8 bg-red-50 p-6 rounded-xl border border-red-100">
              <h4 className="font-bold text-red-600 mb-4 flex items-center gap-2"><i className="fas fa-exclamation-circle"></i> Important Notes</h4>
              <ul className="space-y-2 text-red-800/80 text-[15px]">
                {data.entrance_procedure.notes.map((note, idx) => (
                  <li key={idx} className="flex items-start gap-2"><i className="fas fa-angle-right mt-1"></i> {note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* The Process */}
        {data.process?.length > 0 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-8 flex items-center gap-3">
              <i className="fas fa-cogs text-primary bg-primary/10 w-12 h-12 flex items-center justify-center rounded-xl"></i> 
              The Process
            </h3>
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 before:to-transparent">
              {data.process.map((step, idx) => (
                <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-primary text-slate-100 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 font-bold z-10">{idx + 1}</div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <h4 className="font-bold text-lg mb-1">{step.title}</h4>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {data.post_admission && (
              <div className="mt-12 bg-blue-50/50 border border-blue-100 p-6 rounded-xl">
                <h4 className="font-bold text-xl mb-2 text-primary flex items-center"><i className="fas fa-info-circle mr-2"></i> Post Admission</h4>
                <p className="text-gray-600 text-[15px]">{data.post_admission}</p>
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {data.documents?.length > 0 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-file-alt text-primary bg-primary/10 w-12 h-12 flex items-center justify-center rounded-xl"></i> 
              Documents Required
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {data.documents.map((doc, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-100 rounded-xl hover:bg-white hover:shadow-sm transition">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <i className="fas fa-check text-brand text-lg"></i>
                  </div> 
                  <span className="font-medium text-[15px]">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Transportation */}
        {data.transportation && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-bus text-primary bg-primary/10 w-12 h-12 flex items-center justify-center rounded-xl"></i> 
              Transportation Guidelines
            </h3>
            <p className="text-gray-600 mb-4 text-[15px]">{data.transportation.intro}</p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-5 text-blue-900 rounded-r-lg text-[15px] font-medium leading-relaxed">
              {data.transportation.rules}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdmissionGuidelines;
